import { LENDER_MODE_NO_MODE } from '../../../assets'
import { resolveModeConfig } from './resolveModeConfig'
import { InitUserReserveResponse } from '../../../types/lenderTypes'
import { safeDivide } from './formatting'
import { getOraclePrice } from './oraclePrice'
import {
  buildUnderlyingInfo,
  getMarketUidsFromMeta,
  LenderCrossPoolMeta,
  UserData,
  UserDataForSubAccount,
} from './types'

export function createMultiAccountTypeUserState(
  payload: InitUserReserveResponse,
  lenderData: LenderCrossPoolMeta,
  histData: {
    [a: string]: {
      totalDebt24h: number
      totalDeposits24h: number
    }
  },
): UserData {
  const positionKeys = Object.keys(payload.lendingPositions ?? {})
  let marketUids = getMarketUidsFromMeta(lenderData)
  const { chainId, account } = payload

  let data: UserDataForSubAccount[] = []
  // main user data
  for (let j = 0; j < positionKeys.length; j++) {
    const posId = positionKeys[j]
    // mode is stored separately from positions for clean array serialization
    const posMode = payload.modes?.[posId]
    // position mode can be undefined
    // in this case we default to no mode
    const mode = String(posMode ?? LENDER_MODE_NO_MODE)
    const userConfig = {
      id: posId,
      selectedMode: mode,
      isWhitelisted: true,
    }
    // organic yields
    let depositInterest = 0
    let borrowInterest = 0
    // rewards
    let rewardDepositAccrual = 0
    let rewardBorrowAccrual = 0
    // staking
    let stakingDepositAccrual = 0
    let stakingBorrowAccrual = 0
    // amountrs
    let deposits = 0
    let debt = 0
    let collateral = 0
    let borrowDiscountedCollateral = 0
    let adjustedDebt = 0
    for (let i = 0; i < marketUids.length; i++) {
      const marketUid = marketUids[i]

      if (
        !lenderData?.[marketUid] ||
        !payload.lendingPositions[posId][marketUid]
      )
        continue

      const pos = payload.lendingPositions[posId][marketUid]
      const { depositsUSD, debtUSD } = pos
      // oracle values for risk calculations (fallback to general if missing)
      const depositsUSDOracle = (pos as any).depositsUSDOracle ?? depositsUSD
      const debtUSDOracle = (pos as any).debtUSDOracle ?? debtUSD
      const {
        depositRate,
        intrinsicYield,
        variableBorrowRate,
        rewards,
        configs,
      } = lenderData[marketUid]
      // amounts (display)
      deposits += depositsUSD
      debt += debtUSD
      // rewards
      ;(rewards ?? []).forEach((rewardData: any) => {
        rewardDepositAccrual += rewardData.depositRate * depositsUSD
        rewardBorrowAccrual +=
          rewardData.variableBorrowRate * debtUSD +
          (rewardData.stableBorrowRate ?? 0) * debtUSD
      })
      // staking
      stakingDepositAccrual += (intrinsicYield ?? 0) * depositsUSD
      stakingBorrowAccrual += (intrinsicYield ?? 0) * debtUSD

      // Parameter-aware: on a parameterized market (LlamaLend) `mode` is the
      // position's band count, and the factors come from the config's own
      // curve at that value — a bare `configs[mode]` missed and the `?? 1`
      // fallbacks below then priced collateral undiscounted (health read
      // deposits/debt, borrowable read NAV-shaped).
      const config = resolveModeConfig(configs, mode)

      // risk adjusted (oracle-based for accuracy)
      collateral += (config?.collateralFactor ?? 1) * depositsUSDOracle
      borrowDiscountedCollateral +=
        (config?.borrowCollateralFactor ?? 1) * depositsUSDOracle
      adjustedDebt += (config?.borrowFactor ?? 1) * debtUSDOracle

      // IRs
      depositInterest += depositRate! * depositsUSD
      borrowInterest += debtUSD * variableBorrowRate!
    }
    const nav = deposits - debt

    // aggregated balance data
    const balanceData = {
      borrowDiscountedCollateral,
      borrowDiscountedCollateralAllActive: borrowDiscountedCollateral,
      collateral,
      collateralAllActive: collateral,
      deposits,
      debt,
      adjustedDebt,
      nav,
      deposits24h: histData[posId]?.totalDeposits24h,
      debt24h: histData[posId]?.totalDebt24h,
      nav24h: histData[posId]?.totalDeposits24h - histData[posId]?.totalDebt24h,
    }

    // aggregated apr data
    const aprData = {
      apr: safeDivide(depositInterest - borrowInterest, nav),
      borrowApr: safeDivide(borrowInterest, debt),
      depositApr: safeDivide(depositInterest, deposits),
      rewardApr: safeDivide(rewardDepositAccrual + rewardBorrowAccrual, nav),
      rewardDepositApr: safeDivide(rewardDepositAccrual, deposits),
      rewardBorrowApr: safeDivide(rewardBorrowAccrual, debt),
      intrinsicApr: safeDivide(stakingDepositAccrual - stakingBorrowAccrual, nav),
      intrinsicDepositApr: safeDivide(stakingDepositAccrual, deposits),
      intrinsicBorrowApr: safeDivide(stakingBorrowAccrual, debt),
      rewards: {},
    }

    // enrich positions with withdrawable/borrowable amounts
    const creditLine = Math.max(0, borrowDiscountedCollateral - adjustedDebt)
    for (let i = 0; i < marketUids.length; i++) {
      const marketUid = marketUids[i]
      if (!lenderData?.[marketUid]) continue

      const { configs, flags, borrowLiquidity, withdrawLiquidity } = lenderData[marketUid]
      const config = resolveModeConfig(configs, mode)
      const price = getOraclePrice(lenderData[marketUid])
      const bcf = config?.borrowCollateralFactor ?? 1
      const bf = config?.borrowFactor ?? 1

      const pos = payload.lendingPositions[posId]?.[marketUid]

      if (pos) {
        // if collateral is not enabled (user or config level), deposit is not
        // backing debt → full balance is withdrawable (frozen still allows withdrawal)
        let withdrawable: string
        if (!(pos as any).collateralEnabled || config?.collateralDisabled) {
          withdrawable = String(pos.deposits)
        } else if (balanceData.debt === 0) {
          withdrawable = String(pos.deposits)
        } else {
          const withdrawableUSD = Math.max(creditLine / bcf, 0)
          withdrawable = String(Math.min(withdrawableUSD / price, Number(pos.deposits)))
        }
        // cap by protocol-level withdraw liquidity
        if (withdrawLiquidity != null) {
          withdrawable = String(Math.min(Number(withdrawable), withdrawLiquidity))
        }

        // zero if borrowing disabled, reserve frozen, or debt disabled for mode
        let borrowable: string
        if (!flags?.borrowingEnabled || flags?.isFrozen || config?.debtDisabled) {
          borrowable = '0'
        } else {
          const borrowableUSD = Math.max(creditLine / bf, 0)
          borrowable = String(borrowableUSD / price)
        }
        // cap by protocol-level borrow liquidity
        if (borrowLiquidity != null) {
          borrowable = String(Math.min(Number(borrowable), borrowLiquidity))
        }

        ;(pos as any).withdrawable = withdrawable
        ;(pos as any).borrowable = borrowable
        ;(pos as any).underlyingInfo = buildUnderlyingInfo(lenderData[marketUid])
      } else if (
        (deposits > 0 || debt > 0) &&
        flags?.borrowingEnabled &&
        !flags?.isFrozen &&
        !config?.debtDisabled
      ) {
        let borrowable = String(Math.max(creditLine / bf, 0) / price)
        // cap by protocol-level borrow liquidity
        if (borrowLiquidity != null) {
          borrowable = String(Math.min(Number(borrowable), borrowLiquidity))
        }

        payload.lendingPositions[posId][marketUid] = {
          marketUid,
          deposits: '0',
          debt: '0',
          depositsUSD: 0,
          debtUSD: 0,
          collateralEnabled: false,
          claimableRewards: 0,
          withdrawable: '0',
          borrowable,
          underlyingInfo: buildUnderlyingInfo(lenderData[marketUid]),
        } as any
      }
    }

    data.push({
      accountId: posId,

      health:
        balanceData.debt === 0
          ? null
          : balanceData.adjustedDebt > 0
            ? balanceData.borrowDiscountedCollateral / balanceData.adjustedDebt
            : balanceData.collateral / balanceData.debt,
      borrowCapacityUSD: creditLine,
      userConfig,
      // aggregated balance data
      balanceData,

      // aggregated apr data
      aprData,
      positions: Object.values(payload.lendingPositions[posId]) as any,
    })
  }
  return {
    lender: '',
    account,
    chainId,
    data,
  }
}
